// One shared browser speech helper (Web Speech API — free, no keys needed).
// Used by Easy Mode so seniors can go through the whole app eyes-free.
//
// Master gate: "force_voice_assist" (default ON). AUTOMATIC speech (step
// narration, auto-read, robot chatter) respects it. Explicit user taps
// (speaker buttons, voice commands, wake phrase) pass { force: true }.
//
// AUDIO ARBITER: exactly ONE voice at a time, app-wide. Every speak() claims
// the channel — any robot video playing with sound is told to yield, and any
// in-flight TTS utterance is cancelled (Android-safe) before the new one
// starts. Components can check isAudioActive() / onAudioActive() to avoid
// talking over something (robot video audio counts too).
const ASSIST_KEY = "force_voice_assist";

export function isVoiceAssistOn() {
    return localStorage.getItem(ASSIST_KEY) !== "0";
}

export function setVoiceAssist(on) {
    localStorage.setItem(ASSIST_KEY, on ? "1" : "0");
    if (!on) stopSpeaking();
}

// ---- audio state -----------------------------------------------------------
let ttsSpeaking = false;      // our own speechSynthesis utterance is talking
let robotAudioActive = false; // a robot video is playing WITH sound
const audioListeners = new Set();

function emitAudio() {
    const active = ttsSpeaking || robotAudioActive;
    audioListeners.forEach((fn) => { try { fn(active); } catch { /* noop */ } });
}

/** True while ANY app audio (TTS or audible robot video) is playing. */
export function isAudioActive() {
    return ttsSpeaking || robotAudioActive;
}

/** Subscribe to audio-active changes. Returns an unsubscribe fn. */
export function onAudioActive(fn) {
    audioListeners.add(fn);
    return () => audioListeners.delete(fn);
}

/** Called by the robot when its video starts/stops playing with sound. */
export function setRobotAudioActive(v) {
    const next = !!v;
    if (robotAudioActive !== next) { robotAudioActive = next; emitAudio(); }
}

/** Stop any audible robot video (used when something else must talk). */
export function silenceRobotVideo() {
    try { window.dispatchEvent(new CustomEvent("force:audio-claim")); } catch { /* noop */ }
}

// ---- one consistent voice --------------------------------------------------
// Pin a single TTS voice for the whole app so the assistant never sounds like
// two different people. Prefers the natural Google US English voice on Android.
let pinnedVoice = null;
function getVoice() {
    if (pinnedVoice) return pinnedVoice;
    try {
        const voices = window.speechSynthesis.getVoices() || [];
        pinnedVoice = voices.find((v) => /google us english/i.test(v.name))
            || voices.find((v) => v.lang === "en-US" && v.localService)
            || voices.find((v) => (v.lang || "").replace("_", "-").startsWith("en-US"))
            || voices.find((v) => (v.lang || "").replace("_", "-").startsWith("en"))
            || null;
    } catch { /* noop */ }
    return pinnedVoice;
}
try {
    if ("speechSynthesis" in window) {
        // Voices load async on some browsers — re-pin when the list arrives.
        window.speechSynthesis.onvoiceschanged = () => { pinnedVoice = null; };
    }
} catch { /* noop */ }

// ---- speech ----------------------------------------------------------------
export function speak(text, { rate = 0.92, pitch = 1.05, force = false } = {}) {
    try {
        if (!force && !isVoiceAssistOn()) return;
        if (!("speechSynthesis" in window) || !text) return;
        silenceRobotVideo(); // robot video audio always yields to TTS
        const synth = window.speechSynthesis;
        const go = () => {
            const u = new SpeechSynthesisUtterance(text);
            u.rate = rate;
            u.pitch = pitch;
            const v = getVoice();
            if (v) u.voice = v;
            u.onstart = () => { ttsSpeaking = true; emitAudio(); };
            const off = () => { ttsSpeaking = false; emitAudio(); };
            u.onend = off;
            u.onerror = off;
            synth.speak(u);
        };
        // Android Chrome quirks: cancel() needs a beat before the next speak()
        // (else the old utterance keeps talking and overlaps), and the engine
        // can wedge in a paused state — resume() unsticks it.
        const wasBusy = synth.speaking || synth.pending;
        synth.cancel();
        try { synth.resume(); } catch { /* noop */ }
        if (ttsSpeaking) { ttsSpeaking = false; emitAudio(); }
        if (wasBusy) setTimeout(go, 90);
        else go();
    } catch { /* speech unavailable */ }
}

export function stopSpeaking() {
    try {
        if ("speechSynthesis" in window) window.speechSynthesis.cancel();
        if (ttsSpeaking) { ttsSpeaking = false; emitAudio(); }
    } catch { /* noop */ }
}

// Read a job card aloud: "Fix leaky tap. Plumbing. Pays 120 dollars, in Markham. …"
// (Explicit speaker-button tap — bypasses the voice-assist master toggle.)
export function speakJob(job) {
    if (!job) return;
    speak(`${job.title}. ${job.category || ""}. Pays ${job.budget || 0} dollars, in ${job.city || "your area"}. ${job.description || ""}`, { force: true });
}

// Read a list of jobs aloud, numbered, capped (boards' "Listen" buttons).
export function speakJobList(jobs, intro = "Here are the latest jobs") {
    if (!jobs?.length) { speak("No jobs to read right now.", { force: true }); return; }
    const top = jobs.slice(0, 5)
        .map((j, i) => `Number ${["one", "two", "three", "four", "five"][i]}: ${j.title}. ${j.category || ""}, ${j.budget || 0} dollars, in ${j.city || "your area"}.`)
        .join(" ");
    speak(`${intro}. ${top}${jobs.length > 5 ? ` And ${jobs.length - 5} more on the board.` : ""}`, { force: true });
}
